/**
 * Admin Manual — Premium SaaS Documentation UI
 * Sections match the actual admin sidebar navigation
 * Uses inline styles for layout to beat App.css specificity
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  LayoutDashboard, Users, FileText, ShieldCheck,
  ClipboardList, AlertTriangle, Settings, BookOpen,
  Shield, UserX, UserCheck, Trash2, RefreshCw,
  Search, Download, Lock, Bell, CheckCircle2, Info,
  Database, Eye, Mail, KeyRound, Timer,
  Activity, BarChart3, Files, FolderOpen, Share2,
} from 'lucide-react';

const SECTIONS = [
  { id: 'login',     num: '1.0', title: 'Login & OTP',       icon: Lock },
  { id: 'dashboard', num: '2.0', title: 'Dashboard',          icon: LayoutDashboard },
  { id: 'files',     num: '3.0', title: 'Files',              icon: Files },
  { id: 'users',     num: '4.0', title: 'User Access',        icon: Users },
  { id: 'control',   num: '5.0', title: 'Access Control',     icon: ShieldCheck },
  { id: 'alerts',    num: '6.0', title: 'Security Alerts',    icon: AlertTriangle },
  { id: 'audit',     num: '7.0', title: 'Audit Logs',         icon: ClipboardList },
  { id: 'settings',  num: '8.0', title: 'Settings',           icon: Settings },
];

/* ── Inline style objects (beat App.css specificity) ─────────────── */

const S = {
  card: {
    background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.03)',
    overflow: 'hidden', transition: 'box-shadow 0.25s ease',
  },
  sidebarCard: {
    background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden',
    position: 'sticky', top: 16,
  },
  alertInfo: {
    background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10, padding: '12px 14px',
    display: 'flex', gap: 10, alignItems: 'flex-start',
  },
  alertWarning: {
    background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 14px',
    display: 'flex', gap: 10, alignItems: 'flex-start',
  },
  alertSuccess: {
    background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 14px',
    display: 'flex', gap: 10, alignItems: 'flex-start',
  },
  codeBlock: {
    background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 16,
    fontFamily: 'monospace', fontSize: 12, color: '#94a3b8', lineHeight: 1.7,
    overflowX: 'auto', margin: '12px 0',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  tableWrapper: {
    border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', margin: '8px 0',
  },
  th: {
    textAlign: 'left', padding: '8px 12px', fontSize: 10, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.05em',
    background: '#f8fafc', color: '#64748b', borderBottom: '1px solid #e2e8f0',
  },
  td: {
    padding: '8px 12px', color: '#475569', borderBottom: '1px solid #f1f5f9',
  },
  featureCard: {
    display: 'flex', alignItems: 'flex-start', gap: 10, padding: 12,
    background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: 10,
  },
  featureIcon: {
    flexShrink: 0, width: 28, height: 28, borderRadius: 8,
    background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#6366f1',
  },
  stepNum: {
    flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
    background: '#e0e7ff', color: '#4338ca', fontSize: 11, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  sectionNum: {
    flexShrink: 0, width: 36, height: 36, borderRadius: '50%',
    background: '#4f46e5', color: '#fff', fontSize: 14, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(79,70,229,0.3)',
  },
  subHeading: {
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
    color: '#94a3b8', marginBottom: 10,
  },
  headerGradient: {
    background: 'linear-gradient(135deg, #312e81 0%, #4f46e5 50%, #7c3aed 100%)',
    borderRadius: 16, padding: '24px 28px', marginBottom: 24, color: '#fff',
  },
  statCard: {
    background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14,
  },
};

/* ── Reusable components (all inline-styled) ─────────────────────── */

function SectionCard({ children, id, visible }) {
  return (
    <section id={`am-${id}`} style={{
      ...S.card,
      opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(16px)',
      transition: 'opacity 0.5s ease, transform 0.5s ease, box-shadow 0.25s ease',
      scrollMarginTop: 16,
    }}>
      {children}
    </section>
  );
}

function SectionTitle({ num, title, icon: Icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
      <span style={S.sectionNum}>{num.replace('.0', '')}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
        <Icon size={18} style={{ color: '#6366f1', flexShrink: 0 }} />
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 }}>{title}</h2>
      </div>
    </div>
  );
}

function AlertBox({ type = 'info', children }) {
  const cfg = { info: { s: S.alertInfo, c: '#3730a3', I: Info }, warning: { s: S.alertWarning, c: '#92400e', I: AlertTriangle }, success: { s: S.alertSuccess, c: '#166534', I: CheckCircle2 } }[type];
  return (
    <div style={cfg.s}>
      <cfg.I size={15} style={{ flexShrink: 0, marginTop: 2, color: cfg.c, opacity: 0.7 }} />
      <div style={{ fontSize: 12, lineHeight: 1.6, color: cfg.c }}>{children}</div>
    </div>
  );
}

function CodeBlock({ children }) {
  return <div style={S.codeBlock}>{children}</div>;
}

function StatsGrid({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
      {items.map((item) => (
        <div key={item.label} style={S.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <p style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', margin: 0 }}>{item.label}</p>
            <item.icon size={14} style={{ color: '#818cf8' }} />
          </div>
          <p style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0 }}>{item.value}</p>
          <p style={{ fontSize: 10, color: '#94a3b8', margin: '3px 0 0' }}>{item.desc}</p>
        </div>
      ))}
    </div>
  );
}

function FeatureGrid({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
      {items.map((item) => (
        <div key={item.title} style={S.featureCard}>
          <span style={S.featureIcon}><item.icon size={14} /></span>
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', margin: 0 }}>{item.title}</p>
            <p style={{ fontSize: 11, color: '#64748b', margin: '3px 0 0' }}>{item.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function StepList({ steps }) {
  return (
    <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {steps.map((step, i) => (
        <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span style={S.stepNum}>{i + 1}</span>
          <p style={{ fontSize: 13, color: '#475569', margin: 0, lineHeight: 1.6 }}>{step}</p>
        </li>
      ))}
    </ol>
  );
}

function DataTable({ headers, rows }) {
  return (
    <div style={S.tableWrapper}>
      <table style={S.table}>
        <thead>
          <tr>{headers.map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
              {row.map((cell, j) => <td key={j} style={S.td}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SubHeading({ children }) {
  return <p style={S.subHeading}>{children}</p>;
}

/* ── Main page ───────────────────────────────────────────────────── */

export default function AdminManualPage() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const [visible, setVisible] = useState({});
  const obs = useRef(null);
  const fadeObs = useRef(null);

  useEffect(() => {
    const nodes = SECTIONS.map((s) => document.getElementById(`am-${s.id}`)).filter(Boolean);
    if (!nodes.length) return;
    obs.current = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting);
      if (vis.length) {
        const top = vis.reduce((a, b) => (a.boundingClientRect.top < b.boundingClientRect.top ? a : b));
        setActiveId(top.target.id.replace('am-', ''));
      }
    }, { rootMargin: '-15% 0px -65% 0px', threshold: 0 });
    nodes.forEach((n) => obs.current.observe(n));
    return () => obs.current?.disconnect();
  }, []);

  useEffect(() => {
    const nodes = SECTIONS.map((s) => document.getElementById(`am-${s.id}`)).filter(Boolean);
    if (!nodes.length) return;
    fadeObs.current = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setVisible((prev) => ({ ...prev, [e.target.id.replace('am-', '')]: true }));
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    nodes.forEach((n) => fadeObs.current.observe(n));
    return () => fadeObs.current?.disconnect();
  }, []);

  const scrollTo = useCallback((id) => {
    document.getElementById(`am-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>

      {/* Header */}
      <div style={S.headerGradient}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BookOpen size={22} color="#fff" />
            </span>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: '#fff', margin: 0 }}>Administrator Manual</h1>
              <p style={{ fontSize: 13, color: 'rgba(199,210,254,0.9)', margin: '4px 0 0' }}>Complete guide for managing users, monitoring files, reviewing security alerts, and configuring the CDEaaS platform.</p>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
          {['8 Sections', 'Admin Only', 'Audit-Ready Content', 'Last updated: Apr 2026'].map((t) => (
            <span key={t} style={{ fontSize: 10, fontWeight: 600, background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.9)', padding: '4px 10px', borderRadius: 20 }}>{t}</span>
          ))}
        </div>
      </div>

      {/* Grid: sidebar + content */}
      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 24, alignItems: 'start' }}>

        {/* Sidebar */}
        <nav style={S.sidebarCard}>
          <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #f1f5f9' }}>
            <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', margin: 0 }}>Manual Sections</p>
          </div>
          <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {SECTIONS.map((sec) => {
              const active = sec.id === activeId;
              const Icon = sec.icon;
              return (
                <button key={sec.id} type="button" onClick={() => scrollTo(sec.id)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', textAlign: 'left',
                    fontSize: 12, fontWeight: active ? 600 : 500,
                    background: active ? '#4f46e5' : 'transparent',
                    color: active ? '#fff' : '#64748b',
                    transition: 'all 0.15s ease',
                  }}>
                  <Icon size={13} style={{ color: active ? 'rgba(199,210,254,0.9)' : '#94a3b8' }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sec.title}</span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>

          {/* ═══ 1.0 Login & OTP ═══ */}
          <SectionCard id="login" visible={visible.login !== false}>
            <div style={{ padding: '24px 28px' }}>
              <SectionTitle num="1.0" title="Login & OTP" icon={Lock} />
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.7 }}>
                Admin login uses the same two-step authentication as regular users. You must enter your email and password, then verify with a One-Time Password (OTP) sent to your email. Admin accounts have the same security standards — no shortcuts.
              </p>
              <SubHeading>Key Features</SubHeading>
              <FeatureGrid items={[
                { icon: Mail, title: 'Email & Password', desc: 'Sign in with your admin email credentials' },
                { icon: KeyRound, title: 'OTP Verification', desc: '6-digit code sent to your email for MFA' },
                { icon: Timer, title: 'OTP Expiry', desc: 'Code expires in 5 minutes — request new if needed' },
                { icon: Shield, title: 'Admin-Level Access', desc: 'After OTP, you get admin dashboard and controls' },
              ]} />
              <div style={{ marginTop: 20 }}>
                <SubHeading>How to Login as Admin</SubHeading>
                <StepList steps={[
                  'Open the CDEaaS login page in your browser.',
                  'Enter your admin email address and password, then click Sign In.',
                  'A 6-digit OTP code is sent to your admin email inbox.',
                  'Open your email (check spam if not in inbox) and copy the OTP code.',
                  'Enter the OTP code on the verification screen and click Verify.',
                  'You are redirected to the Admin Dashboard with full administrative controls.',
                ]} />
              </div>
              <div style={{ marginTop: 16 }}>
                <AlertBox type="info">
                  Every admin login is recorded in the <strong>Audit Log</strong> with timestamp, IP address, and user agent. This is automatic and cannot be disabled.
                </AlertBox>
              </div>
            </div>
          </SectionCard>

          {/* ═══ 2.0 Dashboard ═══ */}
          <SectionCard id="dashboard" visible={visible.dashboard !== false}>
            <div style={{ padding: '24px 28px' }}>
              <SectionTitle num="2.0" title="Dashboard" icon={LayoutDashboard} />
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.7 }}>
                The Admin Dashboard is your command center. It shows platform-wide metrics, recent activity across all users, and security alert summaries — giving you a real-time overview of the entire system.
              </p>
              <SubHeading>Platform Metrics</SubHeading>
              <StatsGrid items={[
                { icon: Users, label: 'Total Users', value: '—', desc: 'Registered accounts' },
                { icon: Database, label: 'Total Files', value: '—', desc: 'Encrypted objects' },
                { icon: AlertTriangle, label: 'Active Alerts', value: '—', desc: 'Unresolved incidents' },
                { icon: ShieldCheck, label: 'System Status', value: 'Healthy', desc: 'All checks passing' },
              ]} />
              <div style={{ marginTop: 20 }}>
                <SubHeading>Dashboard Widgets</SubHeading>
                <FeatureGrid items={[
                  { icon: Activity, title: 'Activity Feed', desc: 'Live stream of events across all users' },
                  { icon: Bell, title: 'Alert Badge', desc: 'Topbar bell shows unreviewed security alerts' },
                  { icon: BarChart3, title: 'Trend Charts', desc: 'Login, upload, and OTP failure trends' },
                  { icon: Users, title: 'Quick Actions', desc: 'Jump to Users, Files, or Alerts with one click' },
                ]} />
              </div>
              <div style={{ marginTop: 16 }}>
                <AlertBox type="info">
                  Your first dashboard visit per session logs an <strong>ADMIN_ACCESS</strong> audit event. This is expected behaviour for compliance tracking.
                </AlertBox>
              </div>
            </div>
          </SectionCard>

          {/* ═══ 3.0 Files ═══ */}
          <SectionCard id="files" visible={visible.files !== false}>
            <div style={{ padding: '24px 28px' }}>
              <SectionTitle num="3.0" title="Files" icon={Files} />
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.7 }}>
                The Files page lets you monitor all encrypted files uploaded by users across the platform. You can view file metadata, ownership, and upload dates — but you <strong style={{ color: '#0f172a' }}>cannot decrypt or download</strong> any user's files.
              </p>
              <SubHeading>Key Features</SubHeading>
              <FeatureGrid items={[
                { icon: FolderOpen, title: 'View All Files', desc: 'See every encrypted file in the system' },
                { icon: Users, title: 'File Ownership', desc: 'See which user uploaded each file' },
                { icon: Search, title: 'Search & Filter', desc: 'Find files by name, owner, or date' },
                { icon: Shield, title: 'Zero-Knowledge', desc: 'Admin cannot decrypt — only monitor metadata' },
              ]} />
              <div style={{ marginTop: 20 }}>
                <SubHeading>How to Monitor Files</SubHeading>
                <StepList steps={[
                  'Click "Files" in the admin sidebar.',
                  'Browse the file list — each entry shows filename, owner, size, and upload date.',
                  'Use the search bar to filter by filename or owner email.',
                  'Review file metadata for anomalies (e.g., unusually large uploads, rapid activity).',
                  'Cross-reference suspicious files with the Audit Logs for detailed activity.',
                ]} />
              </div>
              <div style={{ marginTop: 16 }}>
                <AlertBox type="warning">
                  Admins <strong>cannot decrypt user files</strong>. Files are encrypted client-side with user-defined passwords. This is the core zero-knowledge security principle of CDEaaS.
                </AlertBox>
              </div>
            </div>
          </SectionCard>

          {/* ═══ 4.0 User Access ═══ */}
          <SectionCard id="users" visible={visible.users !== false}>
            <div style={{ padding: '24px 28px' }}>
              <SectionTitle num="4.0" title="User Access" icon={Users} />
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.7 }}>
                Manage all registered user accounts. View profile details, suspend problematic accounts, restore deleted users, and monitor per-user activity — all without accessing their encrypted files.
              </p>
              <SubHeading>Available Actions</SubHeading>
              <FeatureGrid items={[
                { icon: UserX, title: 'Suspend User', desc: 'Block login access immediately — data preserved' },
                { icon: UserCheck, title: 'Activate User', desc: 'Re-enable a suspended account' },
                { icon: Trash2, title: 'Delete User', desc: 'Soft-delete — data retained, access revoked' },
                { icon: RefreshCw, title: 'Restore User', desc: 'Reinstate a previously deleted account' },
              ]} />
              <div style={{ marginTop: 20 }}>
                <SubHeading>How to Manage Users</SubHeading>
                <StepList steps={[
                  'Click "User Access" in the admin sidebar.',
                  'Browse the user list or search by email / display name.',
                  'Review user details: role, status, last login, and file count.',
                  'Click Suspend, Activate, Delete, or Restore as appropriate.',
                  'Confirm the action in the dialog — all changes are audit-logged.',
                ]} />
              </div>
              <SubHeading>User Status Reference</SubHeading>
              <DataTable headers={['Status', 'Meaning', 'Can Login?']} rows={[
                ['Active', 'Normal operating account', 'Yes'],
                ['Suspended', 'Temporarily blocked by admin', 'No'],
                ['Deleted', 'Soft-deleted — data retained', 'No'],
              ]} />
              <div style={{ marginTop: 16 }}>
                <AlertBox type="warning">
                  Admins <strong>cannot modify other admin accounts</strong>. Only regular user accounts can be suspended, deleted, or restored. All actions are permanently recorded in the Audit Log.
                </AlertBox>
              </div>
            </div>
          </SectionCard>

          {/* ═══ 5.0 Access Control ═══ */}
          <SectionCard id="control" visible={visible.control !== false}>
            <div style={{ padding: '24px 28px' }}>
              <SectionTitle num="5.0" title="Access Control" icon={ShieldCheck} />
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.7 }}>
                Monitor and manage file-sharing requests between users. As an admin, you can see all access requests across the platform and override decisions when necessary.
              </p>
              <SubHeading>Request Statuses</SubHeading>
              <DataTable headers={['Status', 'Meaning', 'Admin Action']} rows={[
                ['Pending', 'Waiting for file owner response', 'Can override-approve if needed'],
                ['Approved', 'Access granted by file owner', 'Can revoke if policy violated'],
                ['Denied', 'Rejected by file owner', 'Can override-approve if justified'],
              ]} />
              <div style={{ marginTop: 20 }}>
                <SubHeading>Role Permissions</SubHeading>
                <DataTable headers={['Capability', 'User', 'Admin']} rows={[
                  ['Upload & encrypt files', '✓', '✗'],
                  ['Decrypt own files', '✓', '✗'],
                  ['Request file access', '✓', '✗'],
                  ['View personal dashboard', '✓', '✓ (platform-wide)'],
                  ['Manage user accounts', '✗', '✓'],
                  ['View audit logs', '✗', '✓'],
                  ['Review security alerts', '✗', '✓'],
                  ['Override access decisions', '✗', '✓'],
                ]} />
              </div>
              <div style={{ marginTop: 20 }}>
                <SubHeading>How to Manage Access Control</SubHeading>
                <StepList steps={[
                  'Click "Access Control" in the admin sidebar.',
                  'Browse all file-sharing requests across the platform.',
                  'Filter by status (Pending, Approved, Denied) to focus your review.',
                  'Override requests when policy violations or special circumstances justify it.',
                  'All override actions are permanently recorded in the Audit Log.',
                ]} />
              </div>
              <div style={{ marginTop: 16 }}>
                <AlertBox type="info">
                  Admins can manage access <strong>permissions</strong> but still <strong>cannot decrypt files</strong>. Granting access only allows the requester to download the encrypted blob — they still need the file owner's encryption password.
                </AlertBox>
              </div>
            </div>
          </SectionCard>

          {/* ═══ 6.0 Security Alerts ═══ */}
          <SectionCard id="alerts" visible={visible.alerts !== false}>
            <div style={{ padding: '24px 28px' }}>
              <SectionTitle num="6.0" title="Security Alerts" icon={AlertTriangle} />
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.7 }}>
                Your early-warning system. Security alerts are auto-generated for repeated OTP failures, login anomalies, and suspicious access patterns. Triage, investigate, and resolve incidents through a structured lifecycle.
              </p>
              <SubHeading>Alert Lifecycle</SubHeading>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                {[
                  { label: 'NEW', bg: '#fee2e2', color: '#b91c1c' },
                  { label: 'SEEN', bg: '#fef9c3', color: '#a16207' },
                  { label: 'REVIEWED', bg: '#dbeafe', color: '#1d4ed8' },
                  { label: 'RESOLVED', bg: '#dcfce7', color: '#15803d' },
                ].map((s, i) => (
                  <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: s.bg, color: s.color }}>{s.label}</span>
                    {i < 3 && <span style={{ color: '#cbd5e1', fontSize: 14 }}>→</span>}
                  </div>
                ))}
              </div>
              <SubHeading>Severity Levels</SubHeading>
              <DataTable headers={['Level', 'Meaning', 'Response']} rows={[
                ['Low', 'Minor anomaly detected', 'Monitor — no immediate action'],
                ['Medium', 'Possible security concern', 'Investigate and review shortly'],
                ['High', 'Active threat or repeated failures', 'Investigate immediately'],
              ]} />
              <div style={{ marginTop: 20 }}>
                <SubHeading>How to Respond to Alerts</SubHeading>
                <StepList steps={[
                  'Click "Security Alerts" in the admin sidebar — the badge shows unreviewed count.',
                  'Filter by "High" severity to triage critical incidents first.',
                  'Click on each alert to see full details (user, event type, IP, timestamp).',
                  'Cross-reference with Audit Logs if you need more context.',
                  'Click "Mark as Reviewed" to acknowledge, or "Resolve" to close the incident.',
                ]} />
              </div>
              <div style={{ marginTop: 16 }}>
                <AlertBox type="warning">
                  Reviewed and resolved alerts are <strong>never deleted</strong>. They remain in history permanently. The badge counter only reflects NEW / unreviewed alerts.
                </AlertBox>
              </div>
            </div>
          </SectionCard>

          {/* ═══ 7.0 Audit Logs ═══ */}
          <SectionCard id="audit" visible={visible.audit !== false}>
            <div style={{ padding: '24px 28px' }}>
              <SectionTitle num="7.0" title="Audit Logs" icon={ClipboardList} />
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.7 }}>
                The immutable audit trail. Every system event — logins, uploads, decryptions, access requests, and admin actions — is recorded permanently. No entry can be modified or deleted by anyone.
              </p>
              <SubHeading>Tracked Event Types</SubHeading>
              <DataTable headers={['Category', 'Events Logged']} rows={[
                ['Authentication', 'LOGIN, LOGOUT, OTP_SUCCESS, OTP_FAIL'],
                ['File Operations', 'UPLOAD, DECRYPT, DELETE'],
                ['Access Control', 'ACCESS_REQUEST, APPROVE, DENY, REVOKE'],
                ['Admin Actions', 'ADMIN_ACCESS, SUSPEND, DELETE_USER, RESTORE'],
              ]} />
              <div style={{ marginTop: 20 }}>
                <SubHeading>How to Use Audit Logs</SubHeading>
                <StepList steps={[
                  'Click "Audit Logs" in the admin sidebar.',
                  'Apply filters: event type, date range, and/or user email.',
                  'Expand individual rows to see full event details (IP, user agent, outcome).',
                  'Click "Export CSV" or "Export PDF" to download filtered results.',
                  'Store exports as official compliance artefacts for your records.',
                ]} />
              </div>
              <CodeBlock>
                GET /admin/audit-logs?event_type=LOGIN&from=2026-04-01&to=2026-04-09{'\n'}
                → Returns paginated audit entries with IP, user agent, outcome
              </CodeBlock>
              <AlertBox type="info">
                Viewing audit logs is itself an audited event. The trail is <strong>append-only</strong> — no one can delete or modify entries through the application.
              </AlertBox>
            </div>
          </SectionCard>

          {/* ═══ 8.0 Settings ═══ */}
          <SectionCard id="settings" visible={visible.settings !== false}>
            <div style={{ padding: '24px 28px' }}>
              <SectionTitle num="8.0" title="Settings" icon={Settings} />
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.7 }}>
                Manage your admin account preferences and platform configuration. Update your display name, change your password, toggle theme, and review session policies.
              </p>
              <SubHeading>Available Settings</SubHeading>
              <DataTable headers={['Setting', 'Type', 'Description']} rows={[
                ['Display Name', 'Text input', 'Admin name shown in the topbar'],
                ['Dark Mode', 'Toggle', 'Switch between light and dark theme'],
                ['Alert Notifications', 'Toggle', 'Enable/disable security alert notifications'],
                ['Password', 'Form', 'Change your admin account password'],
                ['Session Timeout', 'System (fixed)', '15 min idle — platform security control'],
              ]} />
              <div style={{ marginTop: 20 }}>
                <SubHeading>How to Update Settings</SubHeading>
                <StepList steps={[
                  'Click "Settings" in the admin sidebar.',
                  'Update your display name or toggle preferences as needed.',
                  'Use the "Change Password" form to update your credentials.',
                  'Click Save and verify the success confirmation message.',
                ]} />
              </div>
              <div style={{ marginTop: 16 }}>
                <AlertBox type="info">
                  The 15-minute session timeout is a <strong>platform security control</strong> and cannot be changed by anyone. Always use the Sign Out button when leaving your workstation.
                </AlertBox>
              </div>
            </div>
          </SectionCard>

          {/* Footer */}
          <div style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', padding: '16px 0', borderTop: '1px solid #f1f5f9' }}>
            JOSMY CDEaaS · Administrator Manual · All actions are recorded in the Audit Log · v1.0
          </div>
        </div>
      </div>
    </div>
  );
}
